import { useEffect, useState, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import type { SourceControlProvider } from '@/components/integrations/SourceControlIntegrationCard';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';

interface BitbucketConnectDialogProps {
  provider: SourceControlProvider;
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    accessToken: string;
    tokenExpiresAt: string | null;
  }) => void;
  isLoading?: boolean;
  error?: string;
  /** When true, the connect action is disabled. */
  readOnly?: boolean;
}

export function BitbucketConnectDialog({
  provider,
  organizationId,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  error,
  readOnly = false,
}: BitbucketConnectDialogProps): ReactElement {
  const [accessToken, setAccessToken] = useState('');
  const [tokenExpiresAt, setTokenExpiresAt] = useState('');
  const [expirationUnknown, setExpirationUnknown] = useState(false);

  const { markDirty, markClean } = useUnsavedChangeActions();
  const { changeScopeTo } = useScopeChangeAction();
  const scopeId = `bitbucket-connect-${organizationId}`;
  const hasAccessToken = accessToken.trim().length > 0;
  const isDirty = hasAccessToken;

  useEffect(() => {
    if (isDirty) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      markClean(scopeId);
    };
  }, [isDirty, markDirty, markClean, scopeId]);

  const handleSubmit = (): void => {
    const trimmedToken = accessToken.trim();
    if (!trimmedToken) return;

    onConfirm({
      accessToken: trimmedToken,
      tokenExpiresAt: expirationUnknown ? null : tokenExpiresAt || null,
    });
  };

  const resetFormState = (): void => {
    setAccessToken('');
    setTokenExpiresAt('');
    setExpirationUnknown(false);
    markClean(scopeId);
  };

  const handleClose = (): void => {
    if (isLoading) return;
    if (!isDirty) {
      onClose();
      return;
    }
    changeScopeTo({
      from: scopeId,
      id: 'close-bitbucket-connect',
      title: 'Unsaved changes',
      body: 'You have unsaved changes that will be lost.',
      cancelLabel: 'Keep Editing',
      discardLabel: 'Discard',
      run: () => {
        resetFormState();
        onClose();
      },
    });
  };

  const isSubmitDisabled =
    isLoading || !hasAccessToken || (!expirationUnknown && !tokenExpiresAt);

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect {provider.displayName} Workspace</DialogTitle>
          <DialogDescription>
            Connect your {provider.displayName} workspace by providing a
            workspace access token. Follow the steps below to create one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border bg-muted/50 p-3 text-sm">
            <ol className="list-decimal space-y-1 pl-4 text-muted-foreground">
              <li>
                In Bitbucket, go to{' '}
                <strong className="text-foreground">
                  Workspace Settings &gt; Access Tokens
                </strong>
              </li>
              <li>
                Create a token with{' '}
                <strong className="text-foreground">Read</strong> access to{' '}
                <strong className="text-foreground">Account</strong> and{' '}
                <strong className="text-foreground">Repositories</strong>
              </li>
              <li>Paste the token and expiration date below</li>
            </ol>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bb-access-token">Workspace access token</Label>
            <Input
              id="bb-access-token"
              type="password"
              placeholder="Paste your workspace access token"
              value={accessToken}
              onChange={e => setAccessToken(e.target.value)}
              disabled={isLoading}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bb-token-expires">Token expiration date</Label>
            <div className="flex items-center gap-3">
              <Input
                id="bb-token-expires"
                type="date"
                value={tokenExpiresAt}
                onChange={e => setTokenExpiresAt(e.target.value)}
                disabled={isLoading || expirationUnknown}
                className="flex-1"
              />
              <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
                <Checkbox
                  checked={expirationUnknown}
                  onChange={e =>
                    setExpirationUnknown((e.target as HTMLInputElement).checked)
                  }
                  disabled={isLoading}
                  aria-label="Token expiration unknown"
                />
                <span
                  className="cursor-pointer select-none"
                  onClick={() =>
                    !isLoading && setExpirationUnknown(prev => !prev)
                  }
                  aria-hidden="true"
                >
                  Unknown
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Shown when you create the token in Bitbucket. If unknown, Kirin
              will detect expiration when the token stops working.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled || readOnly}
          >
            {isLoading ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
